import type { ViewQueryNode, ViewQueryRecordNode, With } from '@salescore/core'
import { getInnerRowSpan } from '@salescore/core'
import { Array, HashMap, Option, Order, pipe, Record, Result } from 'effect'
import { isUndefined } from 'effect/Predicate'

import { replaceChildRecordNode } from '../../rsheet/util/replaceChildRecordNode'
import { getRecordNodeFromRootNodePure } from '../../state/nodeUtility'
import type { RSheetRecordNode } from '../column'
import type { UpdateChangeCandidate } from '../generateChangesFromCopy/generateChangeCandidates'
import { calcCellHeight } from './calcCellHeight'

// 既存レコードの高さを UpdateChangeCandidate の情報に基づいて調整する。コピー元のセルの高さをコピー先のセルに適用する際などに使用する。
// セルの高さが更新される場合、コピー先のセルだけでなく、その兄弟レコードも含めて height, innerRowIndexStart, innerRowIndexEnd を調整する必要がある。
export const adjustRecordHeights = (
  records: RSheetRecordNode[],
  updates: Array<With<UpdateChangeCandidate, 'recordId'>>,
): RSheetRecordNode[] => {
  const updatesByNode = pipe(
    updates,
    Array.groupBy((update) => update.column.node.path.join(',')),
    Record.values,
  )
  return pipe(
    updatesByNode,
    Array.reduce(records, (currentRecords, updatesForNode) => {
      const adjusted = adjustRecordHeightsForNode(currentRecords, updatesForNode[0].column.node, updatesForNode)
      return currentRecords.map((record) =>
        pipe(
          HashMap.get(adjusted, record.id ?? ''),
          Option.getOrElse(() => record),
        ),
      )
    }),
  )
}

const adjustRecordHeightsForNode = (
  records: RSheetRecordNode[],
  node: ViewQueryNode,
  updates: Array<With<UpdateChangeCandidate, 'recordId'>>,
): HashMap.HashMap<string, RSheetRecordNode> => {
  // rowIndex, innerRowIndex でソートした updates を取得する
  const orderedUpdates = pipe(
    updates,
    Array.groupBy((update) => `${update.rowIndex}`),
    Record.map((updatesInRow) =>
      pipe(
        updatesInRow,
        Array.sortBy((a, b) => Order.Number(a.innerRowIndex, b.innerRowIndex)),
      ),
    ),
    Record.values,
    Array.sortBy((a, b) => Order.Number(a[0].rowIndex, b[0].rowIndex)),
  )
  return pipe(
    orderedUpdates,
    Array.filterMap((updatesInRow) => {
      const rootRecord = records[updatesInRow[0].rowIndex]
      if (rootRecord === undefined) {
        return Result.failVoid
      }
      return Result.succeed(adjustRecordHeightsForRow(rootRecord, node, updatesInRow))
    }),
    Array.filterMap((adjustedRecord) =>
      isUndefined(adjustedRecord.id) ? Result.failVoid : Result.succeed([adjustedRecord.id, adjustedRecord] as const),
    ),
    HashMap.fromIterable,
  )
}

// rootRecord の中で node に対応するレコードノード(複数の場合もあり)を探す。updates に基づいて（兄弟レコードを含めて）レコードの meta (特に高さとinnerRowIndexStart/End)を調整した新しいルートレコードを返す。 updates は Array.reduce などを用いて作業用レコード配列を更新しながら適用する。
const adjustRecordHeightsForRow = (
  rootRecord: RSheetRecordNode,
  node: ViewQueryNode,
  updates: [With<UpdateChangeCandidate, 'recordId'>, ...Array<With<UpdateChangeCandidate, 'recordId'>>],
): RSheetRecordNode => {
  const siblingRecords = getRecordNodeFromRootNodePure(rootRecord, node.path)
  // 不正なノードパスの場合
  if (siblingRecords === undefined) {
    return rootRecord
  }
  // ルートノードの場合
  if (!Array.isArray(siblingRecords)) {
    const height = calcCellHeight(
      pipe(
        updates,
        Array.map((update) => update.height),
        Array.max(Order.Number),
      ),
      getInnerRowSpan(rootRecord),
    )
    return {
      ...rootRecord,
      meta: {
        ...rootRecord.meta,
        height,
        innerRowIndexStart: 0,
        innerRowIndexEnd: height,
      },
    }
  }
  // 子孫レコードの場合
  const adjustedSiblingRecords = pipe(
    updates,
    Array.reduce(siblingRecords, (currentSiblingRecords, update) => {
      const targetRecordIndex = currentSiblingRecords.findIndex((record) => record.id === update.recordId)
      const targetRecord = currentSiblingRecords[targetRecordIndex]
      // 対応するレコードが見つからない場合は currentSiblingRecords をそのまま返す。しかし、この場合はデータの不整合が起きている可能性があるので、後でエラーとして扱うことを検討する。
      if (targetRecord === undefined) {
        return currentSiblingRecords
      }
      const height = calcCellHeight(update.height, getInnerRowSpan(targetRecord))
      const adjustedRecord = {
        ...targetRecord,
        meta: {
          ...targetRecord.meta,
          height,
          innerRowIndexStart: update.innerRowIndex,
          innerRowIndexEnd: update.innerRowIndex + height,
        },
      }
      return pipe(
        currentSiblingRecords,
        Array.replace(targetRecordIndex, adjustedRecord),
        Option.getOrElse(() => currentSiblingRecords),
        (siblings) => shiftRecordIndices(siblings, targetRecordIndex + 1, height - getInnerRowSpan(targetRecord)),
      )
    }),
  )
  return pipe(
    adjustedSiblingRecords,
    Array.reduce(rootRecord, (currentRecord, adjustedSiblingRecord) => {
      const replaced = replaceChildRecordNode(currentRecord, node.path, adjustedSiblingRecord)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return Result.isFailure(replaced) ? currentRecord : (replaced.success as RSheetRecordNode)
    }),
  )
}

// recordNodes の start 以降のレコードノードの innerRowIndexStart と innerRowIndexEnd を diff だけシフトした新しいレコードノードの配列を返す
const shiftRecordIndices = (recordNodes: ViewQueryRecordNode[], start: number, diff: number): ViewQueryRecordNode[] => {
  const before = recordNodes.slice(0, start)
  const after = recordNodes.slice(start).map((recordNode) => ({
    ...recordNode,
    meta: {
      ...recordNode.meta,
      innerRowIndexStart: recordNode.meta.innerRowIndexStart + diff,
      innerRowIndexEnd: recordNode.meta.innerRowIndexEnd + diff,
    },
  }))
  return [...before, ...after]
}
